/**
 * YARD PIECES (2026-10-19, Matt: "model typical residential trash bins in standard sizes …
 * patio umbrellas (one rectangular and one typical multisided) and lounge chaise (like a
 * poolside lounger)"): three Site Furnishings families, each built from simple solid parts in
 * their own colours (`solidGroups`).
 *
 * - TRASH CART (`trashCart`): the city wheeled cart — a body tapering toward the bottom, a lid
 *   hinged at the back, two wheels and the handle at the back. The anchor (y = 0) is the BACK,
 *   so it parks with its wheels against a wall. Sizes are types (32 / 64 / 96 gal, the standard
 *   cart sizes); the colour is the bin's stream, per placement.
 * - PATIO UMBRELLA (`umbrellaOct` — the octagonal market umbrella, eight ribs, the usual
 *   round one; `umbrellaRect` — rectangular, eight ribs, one at each corner and mid-side): a
 *   pole on a weighted base, the canopy a two-tier hipped shell (steep at the crown, flatter to
 *   the eave), the ribs under it. The canopy size is per placement; the fabric colour too.
 * - CHAISE LOUNGE (`chaise`): a frame on four legs, a seat cushion, and a back cushion hinged
 *   at the head (the anchor end, y = 0) — upright, reclined or flat per placement.
 *
 * Every solid here is a convex piece, so each face's outward side is found from the piece's
 * own centre (`Piece`) rather than hand-wound; the umbrella canopy (not convex) is wound by hand.
 * Local frame (the object convention): x across centred on 0, y from the anchor to the far
 * side, z up from the ground. Plan symbols live here too, so plan and 3D share one layout.
 */
import { MeshBuilder } from '../../extrude'
import { featureEdges } from '../../edges'
import { tintMaterialId, BIN_STREAMS, FABRIC_COLORS, CHAISE_FRAMES, CHAISE_BACKS, type Pt2 } from '@aj/bim-kernel'
import type { SolidGroup } from '../opening/protos'

type V3 = [number, number, number]
const sub = (a: V3, b: V3): V3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]]
const cross = (a: V3, b: V3): V3 => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]]
const dot = (a: V3, b: V3) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2]
const norm = (a: V3): V3 => { const l = Math.hypot(a[0], a[1], a[2]) || 1; return [a[0] / l, a[1] / l, a[2] / l] }

const hexOf = (list: readonly { value: string; hex: string }[], v: unknown, d: string) => (list.find((c) => c.value === v) ?? list.find((c) => c.value === d)!).hex

/** one coloured part list → the proto's solid + its groups */
class Parts {
  private byColor = new Map<string, MeshBuilder>()
  piece(hex: string) {
    let m = this.byColor.get(hex)
    if (!m) { m = new MeshBuilder(); this.byColor.set(hex, m) }
    return new Piece(m)
  }
  mesh(hex: string): MeshBuilder {
    let m = this.byColor.get(hex)
    if (!m) { m = new MeshBuilder(); this.byColor.set(hex, m) }
    return m
  }
  finish(b: MeshBuilder): { groups: SolidGroup[]; edges: Float32Array } {
    const groups: SolidGroup[] = []
    for (const [hex, m] of this.byColor) {
      if (!m.vertCount) continue
      const start = b.idx.length, base = b.vertCount
      for (let i = 0; i < m.pos.length; i++) b.pos.push(m.pos[i])
      for (let i = 0; i < m.nrm.length; i++) b.nrm.push(m.nrm[i])
      for (const i of m.idx) b.idx.push(i + base)
      b.vertCount += m.vertCount
      groups.push({ start, count: m.idx.length, materialId: tintMaterialId(hex, 'default') })
    }
    return { groups, edges: featureEdges(b.build()) }
  }
}

/** a convex solid: every face turns outward from the piece's centre */
class Piece {
  private c: V3 = [0, 0, 0]
  constructor(private m: MeshBuilder) {}
  private face(pts: V3[]) {
    let n = norm(cross(sub(pts[1], pts[0]), sub(pts[2], pts[0])))
    const mid: V3 = [0, 0, 0]
    for (const p of pts) { mid[0] += p[0] / pts.length; mid[1] += p[1] / pts.length; mid[2] += p[2] / pts.length }
    if (dot(n, sub(mid, this.c)) < 0) { pts = [...pts].reverse(); n = [-n[0], -n[1], -n[2]] }
    for (let i = 2; i < pts.length; i++) this.m.addTri(pts[0], pts[i - 1], pts[i], n)
  }
  /** a loft between two rings of the same count (any convex rings, any planes) */
  loft(a: V3[], b: V3[]) {
    const all = [...a, ...b]
    this.c = [0, 0, 0]
    for (const p of all) { this.c[0] += p[0] / all.length; this.c[1] += p[1] / all.length; this.c[2] += p[2] / all.length }
    this.face(a); this.face(b)
    for (let i = 0; i < a.length; i++) {
      const j = (i + 1) % a.length
      this.face([a[i], a[j], b[j], b[i]])
    }
    return this
  }
  box(x0: number, x1: number, y0: number, y1: number, z0: number, z1: number) {
    if (x1 - x0 < 1e-4 || y1 - y0 < 1e-4 || z1 - z0 < 1e-4) return this
    const r = (z: number): V3[] => [[x0, y0, z], [x1, y0, z], [x1, y1, z], [x0, y1, z]]
    return this.loft(r(z0), r(z1))
  }
  /** a round bar from a to b — 16 sides keeps it smooth (no feature edges along it) */
  cyl(a: V3, b: V3, r: number, seg = 16) {
    const t = norm(sub(b, a))
    const u = norm(Math.abs(t[2]) < 0.9 ? cross(t, [0, 0, 1]) : cross(t, [1, 0, 0]))
    const v = cross(t, u)
    const ring = (o: V3): V3[] => Array.from({ length: seg }, (_, k) => {
      const ang = (Math.PI * 2 * k) / seg, cs = Math.cos(ang) * r, sn = Math.sin(ang) * r
      return [o[0] + u[0] * cs + v[0] * sn, o[1] + u[1] * cs + v[1] * sn, o[2] + u[2] * cs + v[2] * sn] as V3
    })
    return this.loft(ring(a), ring(b))
  }
}

// ── trash cart ─────────────────────────────────────────────────────────────────────────

const WHEEL_R = 5 / 12

export function buildTrashCart(w: number, d: number, h: number, b: MeshBuilder, stream: unknown) {
  const P = new Parts()
  const body = hexOf(BIN_STREAMS, stream, 'trash')
  const dark = '#1d1e1f'
  const lidT = 0.1, lidZ = h - lidT
  const yb = 0.12                                     // the body's back, in front of the wheels
  // the body: full size under the lid, narrower and shallower at the foot
  const top: V3[] = [[-w / 2 + 0.03, yb, lidZ], [w / 2 - 0.03, yb, lidZ], [w / 2 - 0.03, d - 0.04, lidZ], [-w / 2 + 0.03, d - 0.04, lidZ]]
  const foot: V3[] = [[-w / 2 + 0.16, yb + 0.06, 0.15], [w / 2 - 0.16, yb + 0.06, 0.15], [w / 2 - 0.16, d - 0.3, 0.15], [-w / 2 + 0.16, d - 0.3, 0.15]]
  P.piece(body).loft(foot, top)
  // the lid (overhangs the front for the grip) and its hinge rail at the back
  P.piece(body).box(-w / 2, w / 2, 0.04, d, lidZ, h)
  P.piece(body).box(-w / 2 + 0.05, w / 2 - 0.05, d - 0.06, d + 0.02, lidZ - 0.12, lidZ)
  P.piece(body).box(-w / 2 + 0.04, w / 2 - 0.04, 0.02, yb + 0.02, lidZ - 0.35, lidZ)
  // the handle across the back, on two stand-offs
  P.piece(dark).cyl([-w / 2 + 0.12, 0.0, h - 0.18], [w / 2 - 0.12, 0.0, h - 0.18], 0.06)
  for (const sx of [-1, 1]) P.piece(body).box(sx * (w / 2 - 0.16) - 0.04, sx * (w / 2 - 0.16) + 0.04, 0.0, yb, h - 0.24, h - 0.12)
  // the axle housing and two wheels at the back corners
  P.piece(body).box(-w / 2 + 0.1, w / 2 - 0.1, yb - 0.02, yb + 0.22, WHEEL_R - 0.12, WHEEL_R + 0.18)
  P.piece(dark).cyl([-w / 2 + 0.12, yb + 0.05, WHEEL_R], [w / 2 - 0.12, yb + 0.05, WHEEL_R], 0.04)
  for (const sx of [-1, 1]) {
    const x0 = sx * (w / 2 - 0.02), x1 = sx * (w / 2 - 0.18)
    P.piece(dark).cyl([x0, yb + 0.05, WHEEL_R], [x1, yb + 0.05, WHEEL_R], WHEEL_R, 20)
  }
  // the front foot the cart stands on when parked
  P.piece(body).box(-w / 2 + 0.2, w / 2 - 0.2, d - 0.42, d - 0.26, 0, 0.15)
  return P.finish(b)
}

export function trashCartPlanSymbol(w: number, d: number): { pts: Pt2[]; closed?: boolean; dashed?: boolean }[] {
  return [
    { pts: [[-w / 2, 0.04], [w / 2, 0.04], [w / 2, d], [-w / 2, d]], closed: true },        // the lid
    { pts: [[-w / 2, 0.14], [w / 2, 0.14]] },                                                // its hinge
    { pts: [[-w / 2 + 0.12, -0.02], [w / 2 - 0.12, -0.02]] },                                // the handle
  ]
}

// ── patio umbrella ─────────────────────────────────────────────────────────────────────

/** the canopy's outline points (CCW), centred on (0, cy): an octagon, or a rectangle with a
 *  rib at every corner and mid-side */
function canopyRing(rect: boolean, hx: number, hy: number, cy: number, k: number): Pt2[] {
  if (rect) {
    return [[hx, 0], [hx, hy], [0, hy], [-hx, hy], [-hx, 0], [-hx, -hy], [0, -hy], [hx, -hy]]
      .map(([x, y]) => [x * k, cy + y * k] as Pt2)
  }
  return Array.from({ length: 8 }, (_, i) => {
    const a = (Math.PI * 2 * i) / 8 + Math.PI / 8
    return [Math.cos(a) * hx * k, cy + Math.sin(a) * hy * k] as Pt2
  })
}

/** the umbrella's layout: the eave ring, the crease ring and the crown, shared by plan and 3D */
export function umbrellaLayout(rect: boolean, w: number, d: number, h: number) {
  const hx = rect ? w / 2 : Math.min(w, d) / 2
  const hy = rect ? d / 2 : Math.min(w, d) / 2
  const cy = d / 2
  const crownZ = h - 0.3
  const eaveZ = Math.max(5.5, h - 0.35 - Math.max(hx, hy) * 0.32)
  const creaseK = 0.45
  const creaseZ = crownZ - (crownZ - eaveZ) * 0.62
  return {
    cy, crownZ, eaveZ, creaseZ,
    eave: canopyRing(rect, hx, hy, cy, 1),
    crease: canopyRing(rect, hx, hy, cy, creaseK),
    hubZ: eaveZ - 0.9,
  }
}

export function buildUmbrella(rect: boolean, w: number, d: number, h: number, b: MeshBuilder, fabric: unknown) {
  const P = new Parts()
  const cloth = hexOf(FABRIC_COLORS, fabric, 'natural')
  const metal = '#3a3b3c'
  const L = umbrellaLayout(rect, w, d, h)
  const cy = L.cy
  // the weighted base and the pole up to the crown
  P.piece(metal).cyl([0, cy, 0], [0, cy, 0.25], 0.85, 24)
  P.piece(metal).cyl([0, cy, 0.25], [0, cy, L.crownZ + 0.08], 0.06)
  P.piece(cloth).cyl([0, cy, L.crownZ + 0.05], [0, cy, L.crownZ + 0.28], 0.1)       // the crown vent
  // the hub and the ribs out to the eave, with the struts back to the hub
  P.piece(metal).cyl([0, cy, L.hubZ - 0.1], [0, cy, L.hubZ + 0.1], 0.1)
  for (let i = 0; i < L.eave.length; i++) {
    const e = L.eave[i], c = L.crease[i]
    P.piece(metal).cyl([0, cy, L.crownZ - 0.05], [c[0], c[1], L.creaseZ - 0.05], 0.025, 8)
    P.piece(metal).cyl([c[0], c[1], L.creaseZ - 0.05], [e[0], e[1], L.eaveZ - 0.04], 0.025, 8)
    P.piece(metal).cyl([0, cy, L.hubZ], [c[0] * 1.15, cy + (c[1] - cy) * 1.15, L.creaseZ - 0.12], 0.02, 8)
  }
  // the canopy: a shell with a top and an underside 1" apart, and its edge band
  const m = P.mesh(cloth)
  const T = 1 / 12
  const tri = (a: V3, bb: V3, c: V3) => m.addTri(a, bb, c, norm(cross(sub(bb, a), sub(c, a))))
  const n = L.eave.length
  const apex: V3 = [0, cy, L.crownZ]
  const lvl = (ring: Pt2[], z: number): V3[] => ring.map((p) => [p[0], p[1], z])
  const cT = lvl(L.crease, L.creaseZ), eT = lvl(L.eave, L.eaveZ)
  const apexB: V3 = [0, cy, L.crownZ - T], cB = lvl(L.crease, L.creaseZ - T), eB = lvl(L.eave, L.eaveZ - T)
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n
    tri(apex, cT[i], cT[j]); tri(cT[i], eT[i], eT[j]); tri(cT[i], eT[j], cT[j])           // top
    tri(apexB, cB[j], cB[i]); tri(cB[i], eB[j], eB[i]); tri(cB[i], cB[j], eB[j])          // underside
    tri(eB[i], eB[j], eT[j]); tri(eB[i], eT[j], eT[i])                                    // edge band
  }
  return P.finish(b)
}

export function umbrellaPlanSymbol(rect: boolean, w: number, d: number, h: number): { pts: Pt2[]; closed?: boolean; dashed?: boolean }[] {
  const L = umbrellaLayout(rect, w, d, h)
  const out: { pts: Pt2[]; closed?: boolean; dashed?: boolean }[] = []
  // the canopy is overhead — dashed, with its ribs
  out.push({ pts: L.eave, closed: true, dashed: true })
  for (const e of L.eave) out.push({ pts: [[0, L.cy], e], dashed: true })
  const circle = (r: number): Pt2[] => Array.from({ length: 16 }, (_, i) => {
    const a = (Math.PI * 2 * i) / 16
    return [Math.cos(a) * r, L.cy + Math.sin(a) * r] as Pt2
  })
  out.push({ pts: circle(0.85), closed: true })     // the base
  out.push({ pts: circle(0.08), closed: true })     // the pole
  return out
}

// ── chaise lounge ──────────────────────────────────────────────────────────────────────

/** the seat height (top of the frame) and the back's hinge, from the head end */
const CHAISE_FRAME_Z = 0.85
const CHAISE_CUSHION = 0.28
const CHAISE_BACK_LEN = 2.3

const backLenFt = (a: number, yh: number) => Math.min(CHAISE_BACK_LEN, yh / Math.max(0.05, Math.cos(a)))

export function chaiseBackDeg(back: unknown): number {
  return (CHAISE_BACKS.find((c) => c.value === back) ?? CHAISE_BACKS[0]).deg
}

export function buildChaise(w: number, d: number, b: MeshBuilder, opts: { back: unknown; cushion: unknown; frame: unknown }) {
  const P = new Parts()
  const frame = hexOf(CHAISE_FRAMES, opts.frame, 'teak')
  const cushion = hexOf(FABRIC_COLORS, opts.cushion, 'white')
  const rail = 0.15, legW = 0.15
  const z1 = CHAISE_FRAME_Z, z0 = z1 - 0.2
  // two side rails and the end rails
  for (const sx of [-1, 1]) P.piece(frame).box(sx > 0 ? w / 2 - rail : -w / 2, sx > 0 ? w / 2 : -w / 2 + rail, 0, d, z0, z1)
  P.piece(frame).box(-w / 2, w / 2, d - rail, d, z0, z1)
  P.piece(frame).box(-w / 2, w / 2, 0, rail, z0, z1)
  // four legs
  for (const sx of [-1, 1]) for (const y of [0.1, d - 0.1 - legW]) {
    const x = sx > 0 ? w / 2 - legW : -w / 2
    P.piece(frame).box(x, x + legW, y, y + legW, 0, z0)
  }
  // the seat cushion, from the back's hinge to the foot
  const yh = Math.min(CHAISE_BACK_LEN, d * 0.38)
  P.piece(cushion).box(-w / 2 + 0.06, w / 2 - 0.06, yh, d - 0.05, z1, z1 + CHAISE_CUSHION)
  // the back cushion, hinged at yh and rising toward the head end
  const a = (chaiseBackDeg(opts.back) * Math.PI) / 180
  const dir: Pt2 = [-Math.cos(a), Math.sin(a)]
  const nrm: Pt2 = [Math.sin(a), Math.cos(a)]
  // as long as the back is, but never past the head end of the frame
  const blen = backLenFt(a, yh)
  const p0: Pt2 = [yh, z1], p1: Pt2 = [yh + dir[0] * blen, z1 + dir[1] * blen]
  const q = (p: Pt2, t: number): Pt2 => [p[0] + nrm[0] * t, p[1] + nrm[1] * t]
  const yz = [p0, p1, q(p1, CHAISE_CUSHION), q(p0, CHAISE_CUSHION)]
  const at = (x: number): V3[] => yz.map(([y, z]) => [x, y, z] as V3)
  P.piece(cushion).loft(at(-w / 2 + 0.06), at(w / 2 - 0.06))
  // the back's prop under it (frame colour)
  if (a > 0.2) {
    const mid: Pt2 = [yh + dir[0] * blen * 0.6, z1 + dir[1] * blen * 0.6]
    for (const sx of [-1, 1]) P.piece(frame).cyl([sx * (w / 2 - 0.2), mid[0], mid[1] - 0.02], [sx * (w / 2 - 0.2), Math.max(0.05, mid[0] - 0.25), z1 - 0.05], 0.035, 8)
  }
  return P.finish(b)
}

/** the chaise's highest point — the back cushion's top edge, or the seat cushion */
export function chaiseTopFt(back: unknown, d: number): number {
  const a = (chaiseBackDeg(back) * Math.PI) / 180
  const yh = Math.min(CHAISE_BACK_LEN, d * 0.38)
  const blen = backLenFt(a, yh)
  return CHAISE_FRAME_Z + Math.max(CHAISE_CUSHION, Math.sin(a) * blen + Math.cos(a) * CHAISE_CUSHION)
}

export function chaisePlanSymbol(w: number, d: number): { pts: Pt2[]; closed?: boolean; dashed?: boolean }[] {
  const yh = Math.min(CHAISE_BACK_LEN, d * 0.38)
  return [
    { pts: [[-w / 2, 0], [w / 2, 0], [w / 2, d], [-w / 2, d]], closed: true },                      // the frame
    { pts: [[-w / 2 + 0.06, 0.05], [w / 2 - 0.06, 0.05], [w / 2 - 0.06, d - 0.05], [-w / 2 + 0.06, d - 0.05]], closed: true },  // the cushion
    { pts: [[-w / 2 + 0.06, yh], [w / 2 - 0.06, yh]] },                                               // the back's hinge
  ]
}
